/* eslint-disable no-invalid-this */
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import { propTypeslocalization } from '../../../constants/Proptypes';

import { FaCookieBite } from 'react-icons/fa';
import Button, { ButtonAppearance } from '../../../../ui/Atoms/Button';
import Icon from '../../../../ui/Atoms/Icon';
import Switch from '../../../../ui/Atoms/Switch';
import { TextAppearance } from '../../../../ui/Atoms/Text';
import {
  addEventListener,
  CookieNames,
  getCookie,
  removeEventListener,
  setCookie,
} from '../../../utils/cookies';
import { gtm } from '../../../utils/gtm';

import { Color } from '../../../constants/Colors';
import * as S from './CookieBanner.styles';

const Text = React.lazy(() => import('../../../../ui/Atoms/Text'));

const GTM_COOKIE_CONSENT_EVENT = 'cookie-consent';

export class CookieBanner extends PureComponent {
  static propTypes = {
    /**
     * Function to hide the cookie banner
     */
    hideCookieBanner: PropTypes.func,
    /**
     * Localization document.
     */
    localization: propTypeslocalization,
    /**
     * Show the cookie banner if true
     */
    showCookieBanner: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    if (!props.localization) {
      return null;
    }

    this.state = {};
  }

  componentDidMount() {
    const { hasUpdated, hasConsented, hasCustomized, ...parsedConsentCookie } =
      this.getState();

    // This is not very clean, but necessary because of SSR.
    this.updateState({ ...this.getState() });

    if (hasConsented === true) {
      gtm({
        event: GTM_COOKIE_CONSENT_EVENT,
        // eslint-disable-next-line camelcase
        event_value: 'website',
        ...parsedConsentCookie,
        essential: true,
      });
    }

    addEventListener(this.onCookieChanged);
  }

  componentWillUnmount() {
    removeEventListener(this.onCookieChanged);
  }

  updateState(state) {
    this.setState(state);
  }

  onCookieChanged = () => this.setState(this.getState());

  getState = () => {
    const parsedConsentCookie = this.getParsedConsentCookie();
    const hasConsented = this.getHasConsented(parsedConsentCookie);

    return {
      hasUpdated: true,
      hasCustomized: false,
      hasConsented,
      enableFunctionalityCookies: parsedConsentCookie.functionality ?? false,
      enablePerformanceCookies: parsedConsentCookie.performance ?? false,
      enableTrackingCookies: parsedConsentCookie.tracking ?? false,
    };
  };

  getHasConsented = parsedConsentCookie =>
    typeof parsedConsentCookie.functionality === 'boolean' &&
    typeof parsedConsentCookie.performance === 'boolean' &&
    typeof parsedConsentCookie.tracking === 'boolean';

  getParsedConsentCookie = () => {
    const rawConsentCookie = getCookie(CookieNames.CONSENT);
    return JSON.parse(
      // Note: Force users of the previous consent cookie version to migrate to new version
      typeof rawConsentCookie === 'string' &&
        ['true', '1'].includes(rawConsentCookie) === false
        ? rawConsentCookie
        : '{}'
    );
  };

  onCookieSwitch = scope => {
    const fieldName = `enable${scope[0].toUpperCase()}${scope.slice(1)}Cookies`;
    const { [fieldName]: currentValue } = this.state;

    if (typeof currentValue === 'boolean') {
      this.setState({ [fieldName]: !currentValue });
    }
  };

  setCookieConsent = ({
    enableFunctionalityCookies,
    enablePerformanceCookies,
    enableTrackingCookies,
  }) => {
    const parsedConsentCookie = {
      functionality: enableFunctionalityCookies,
      performance: enablePerformanceCookies,
      tracking: enableTrackingCookies,
    };
    const rawConsentCookie = JSON.stringify(parsedConsentCookie);

    const { hideCookieBanner } = this.props;

    setCookie(CookieNames.CONSENT, rawConsentCookie, 365, 'None');

    gtm(
      {
        event: GTM_COOKIE_CONSENT_EVENT,
        // eslint-disable-next-line camelcase
        event_value: 'website',
        ...parsedConsentCookie,
        essential: true,
      },
      this.afterCookieConsent
    );

    this.setState({ hasConsented: true });
    hideCookieBanner();
  };

  afterCookieConsent = () => {
    // TODO: configure loaded tools via GTM for the user if necessary.
  };

  onCookieConsent = evt => {
    evt.preventDefault();
    this.setCookieConsent(this.state);
  };

  onCookieConsentEssential = evt => {
    evt.preventDefault();
    this.setCookieConsent({
      enableFunctionalityCookies: false,
      enablePerformanceCookies: false,
      enableTrackingCookies: false,
    });
  };

  onCookieConsentAll = evt => {
    evt.preventDefault();
    this.setCookieConsent({
      enableFunctionalityCookies: true,
      enablePerformanceCookies: true,
      enableTrackingCookies: true,
    });
  };

  onCookieCustomize = () => this.setState({ hasCustomized: true });

  onCancel = () => this.setState({ hasCustomized: false });

  render() {
    const {
      hasUpdated,
      hasCustomized,
      hasConsented,
      enableFunctionalityCookies,
      enablePerformanceCookies,
      enableTrackingCookies,
    } = this.state;

    const { localization, showCookieBanner } = this.props;

    if (hasConsented && !showCookieBanner) {
      return null;
    }

    const {
      data: {
        cookie_button_reject: buttonReject,
        cookie_button_accept: buttonAccept,
        cookie_button_confirm: buttonConfirm,
        cookie_button_back: buttonBack,
        cookie_button_custom: buttonCustom,
        cookie_intro: { html: intro } = {},
        essential_label: essential,
        essential_description: essentialDescription,
        functional_label: functional,
        functional_description: functionalDescription,
        performance_label: performance,
        performance_description: performanceDescription,
        tracking_label: tracking,
        tracking_description: trackingDescription,
      } = {},
    } = localization || '';

    return (
      <>
        {hasUpdated && (
          <S.Modal>
            <Icon customColor={Color.WHITE} hasGradientBackground={true}>
              <FaCookieBite />
            </Icon>
            <React.Suspense fallback='Loading...'>
              <Text type={TextAppearance.NORMAL}>{intro}</Text>
            </React.Suspense>
            {hasCustomized === true ? (
              <S.CustomizationContainer>
                <S.SwitchesWrapper>
                  <S.Switch>
                    <Switch
                      isDefaultChecked={true}
                      isDisabled={true}
                      onClick={() => this.onCookieSwitch('essential')}
                    />
                    <S.SwitchDescription>{essential}</S.SwitchDescription>
                  </S.Switch>
                  <S.SwitchInfo>{essentialDescription}</S.SwitchInfo>
                  <S.Switch>
                    <Switch
                      isDefaultChecked={enableFunctionalityCookies}
                      onClick={() => this.onCookieSwitch('functionality')}
                    />
                    <S.SwitchDescription>{functional}</S.SwitchDescription>
                  </S.Switch>
                  <S.SwitchInfo>{functionalDescription}</S.SwitchInfo>
                  <S.Switch>
                    <Switch
                      isDefaultChecked={enablePerformanceCookies}
                      onClick={() => this.onCookieSwitch('performance')}
                    />
                    <S.SwitchDescription>{performance}</S.SwitchDescription>
                  </S.Switch>
                  <S.SwitchInfo>{performanceDescription}</S.SwitchInfo>
                  <S.Switch>
                    <Switch
                      isDefaultChecked={enableTrackingCookies}
                      onClick={() => this.onCookieSwitch('tracking')}
                    />
                    <S.SwitchDescription>{tracking}</S.SwitchDescription>
                  </S.Switch>
                  <S.SwitchInfo>{trackingDescription}</S.SwitchInfo>
                </S.SwitchesWrapper>
                <S.CustomizationButtonWrapper>
                  <Button
                    appearance={ButtonAppearance.OUTLINE}
                    onClick={this.onCancel}
                  >
                    {buttonBack}
                  </Button>
                  <Button onClick={this.onCookieConsent}>
                    {buttonConfirm}
                  </Button>
                </S.CustomizationButtonWrapper>
              </S.CustomizationContainer>
            ) : (
              <S.ButtonWrapper>
                <Button
                  appearance={ButtonAppearance.OUTLINE}
                  onClick={this.onCookieConsentAll}
                >
                  {buttonAccept}
                </Button>
                <Button
                  appearance={ButtonAppearance.OUTLINE}
                  onClick={this.onCookieConsentEssential}
                >
                  {buttonReject}
                </Button>
                <Button
                  appearance={ButtonAppearance.OUTLINE}
                  onClick={this.onCookieCustomize}
                >
                  {buttonCustom}
                </Button>
              </S.ButtonWrapper>
            )}
          </S.Modal>
        )}
      </>
    );
  }
}
